import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Camera, ImageIcon, Sparkles, Mic, Loader2, X } from 'lucide-react';
import ImageGenerationModal from './ImageGenerationModal';
import { toast } from 'sonner';

interface MessageInputProps {
  onSendMessage: (message: string, options: { model: string; imageData?: string | null }) => void;
  onGenerateImage?: (imageUrls: string[], prompt: string) => void;
  isGenerating: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage, onGenerateImage, isGenerating }) => {
  const [message, setMessage] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'; // Reset height
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 150; // Max height before scrolling (approx 6 lines)
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [message]);

  const handleSendMessage = () => {
    if (message.trim() === '' && !imagePreview) return; // Allow sending just image
    
    console.log("Sending message with image data of length:", imagePreview ? imagePreview.length : 0);
    if (imagePreview) {
      console.log("Image preview starts with:", imagePreview.substring(0, 50));
    }
    
    onSendMessage(message, {
      model: 'MiniMax-Text-01',
      imageData: imagePreview,
    });
    
    setMessage('');
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Reset textarea height after sending
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const base64Data = event.target.result as string;
        setImagePreview(base64Data);
        console.log("Image loaded as Base64, length:", base64Data.length);
        console.log("Image format:", base64Data.substring(0, 30) + "...");
        
        // Validate the base64 format
        if (!base64Data.startsWith('data:image')) {
          console.error("Image data doesn't have the expected format");
        }
      }
    };
    reader.onerror = () => {
      console.error("Error reading file");
      alert("Failed to read the image file");
    };
    reader.readAsDataURL(file);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full bg-transparent rounded-full p-2 mt-4">
      {imagePreview && (
        <div className="relative px-4 pt-2 inline-block">
          <img 
            src={imagePreview} 
            alt="Preview" 
            className="h-24 rounded-md object-cover border border-neon-purple/30" 
          />
          <button 
            className="absolute top-0 right-2 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
            onClick={removeImage}
            aria-label="Remove image"
          >
            ×
          </button>
        </div>
      )}
      
      <div className="relative">
        {/* Enhanced glow effect */}
        <div className="absolute -inset-1 rounded-xl bg-neon-glow-radial opacity-100 blur-xl"></div>
        
        {/* Secondary glow layer - REMOVED */}
        
        <div className="relative flex items-center gap-1 sm:gap-2 px-2 sm:px-4 pb-1 pt-1 z-10 rounded-xl shadow-neon-intense border border-neon-purple/60 bg-[#111111]/30 backdrop-blur-sm"> 
          <div className="flex-1">
            <Textarea
              ref={textareaRef}
              placeholder="Ask anything..."
              className="min-h-[40px] max-h-[150px] focus-visible:ring-0 border-0 bg-transparent rounded-xl resize-none py-1 sm:py-2 px-2 sm:px-3 text-sm sm:text-base overflow-y-auto"
              rows={1} // Start with one row
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isGenerating}
              style={{ 
                fontSize: '16px', // Prevent zoom on mobile iOS
                touchAction: 'manipulation' // Better touch behavior
              }}
            />
          </div>
          
          <div className="flex items-center gap-4 sm:gap-5 self-end pb-1 pr-8 sm:pr-10"> {/* Increased gap and padding */}
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept="image/*"
            />
            
            {/* Image upload button */}
            <Button
              size="icon"
              variant="ghost"
              className="rounded-full h-10 w-10 sm:h-11 sm:w-11 hover:bg-neon-pink/20 text-neon-pink transition-colors flex-shrink-0"
              onClick={handleImageClick}
              disabled={isGenerating}
              aria-label="Upload image"
            >
              <ImageIcon size={28} className="sm:size-[32px]" />
            </Button>
            
            {/* Image generation button - updated with better spacing and styling */}
            <Button
              size="icon"
              variant="ghost"
              className="rounded-full h-10 w-10 sm:h-11 sm:w-11 hover:bg-neon-orange/20 text-neon-orange transition-colors flex-shrink-0"
              onClick={() => setIsImageModalOpen(true)}
              disabled={isGenerating}
              aria-label="Generate image"
            >
              <img 
                src="/pixel-icon.png" 
                alt="Pixel" 
                className="h-10 w-10 sm:h-[44px] sm:w-[44px] object-contain"
              />
            </Button>
            
            {/* Voice agent button - REMOVED */}
            
            {/* Send button */}
            <Button
              className="absolute right-[-12px] sm:right-[-28px] flex-shrink-0 p-0 bg-transparent hover:bg-transparent disabled:opacity-100"
              onClick={handleSendMessage}
              disabled={(message.trim() === '' && !imagePreview) || isGenerating}
              aria-label="Send message"
            >
              <img 
                src="/send-icon.png" 
                alt="Send" 
                className="h-10 w-10 sm:h-20 sm:w-20 object-contain"
              />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Image generation modal */}
      <ImageGenerationModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        onImageGenerated={(imageUrls, prompt) => {
          if (onGenerateImage) {
            onGenerateImage(imageUrls, prompt);
          }
        }}
      />
    </div>
  );
};

export default MessageInput;
